import React, { useState, useEffect } from 'react';
import { devError } from '../lib/devLog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PhoneField, { isPhoneValid } from '../components/PhoneField';
import ProgressSteps from '../components/consign/ProgressSteps';
import PhotoSlot from '../components/consign/PhotoSlot';
import ConsignStep2ArtworkDetails from '../components/consign/ConsignStep2ArtworkDetails';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, Camera, CheckCircle, ArrowRight, ArrowLeft, 
  X, Loader, AlertCircle, Image as ImageIcon, FileText 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConsignPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledArtist = searchParams.get('artist') || '';

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    artist_name: prefilledArtist,
    title: '',
    year: '',
    medium: '',
    dimensions: '',
    edition_number: '',
    provenance: '',
    condition: '',
    purchase_history: '',
    current_location: '',
    name: '',
    email: '',
    phone: ''
  });

  // Image files
  const [artworkImages, setArtworkImages] = useState([]);
  const [markImages, setMarkImages] = useState([]);
  const [slots, setSlots] = useState({ front: null, back: null, edition: null, signature: null });
  const setSlot = (key) => async (file) => {
    setSlots((prev) => ({ ...prev, [key]: file }));
    if (key === 'front' && file && !skipAI && !aiSuggestions) await analyzeFirstImage(file);
  };
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [skipAI, setSkipAI] = useState(false); // Option to skip AI analysis

  const handleImageUpload = async (e, isMainArtwork = true) => {
    const files = Array.from(e.target.files);
    
    if (isMainArtwork) {
      if (artworkImages.length + files.length > 4) {
        alert('Maximum 4 extra artwork photos allowed');
        return;
      }
      setArtworkImages([...artworkImages, ...files]);
    } else {
      if (markImages.length + files.length > 4) {
        alert('Maximum 4 extra close-up photos allowed');
        return;
      }
      setMarkImages([...markImages, ...files]);
    }
  };

  const analyzeFirstImage = async (file) => {
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/consignment/analyze-image`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      
      if (data.success) {
        // Parse AI response (handle both JSON string and object)
        let analysis;
        try {
          analysis = typeof data.analysis === 'string' 
            ? JSON.parse(data.analysis.replace(/```json|```/g, '').trim())
            : data.analysis;
        } catch (e) {
          devError('Could not parse AI response:', e);
          analysis = {};
        }

        setAiSuggestions(analysis);
        
        // Auto-fill form with AI suggestions
        setFormData(prev => ({
          ...prev,
          artist_name: analysis.artist || prev.artist_name,
          title: analysis.title || prev.title,
          medium: analysis.medium || prev.medium,
          edition_number: analysis.edition_number || prev.edition_number,
          condition: analysis.condition || prev.condition
        }));
      }
    } catch (error) {
      devError('AI analysis error:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const removeImage = (index, isArtwork = true) => {
    if (isArtwork) {
      setArtworkImages(artworkImages.filter((_, i) => i !== index));
    } else {
      setMarkImages(markImages.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async () => {
    if (!isPhoneValid(formData.phone)) {
      alert('Please enter a valid phone number with country code.');
      return;
    }
    setLoading(true);
    try {
      const submitData = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });

      // Required labelled photos, then any extras
      submitData.append('front_image', slots.front);
      submitData.append('back_image', slots.back);
      submitData.append('edition_image', slots.edition);
      submitData.append('signature_image', slots.signature);
      artworkImages.forEach(file => {
        submitData.append('artwork_images', file);
      });

      markImages.forEach(file => {
        submitData.append('mark_images', file);
      });

      const response = await fetch(`${API_URL}/api/consignment/submit`, {
        method: 'POST',
        body: submitData
      });

      if (!response.ok) {
        let errMsg = `Submission failed (${response.status})`;
        try {
          const errJson = await response.json();
          if (typeof errJson.detail === 'string') errMsg = errJson.detail;
          else if (Array.isArray(errJson.detail)) errMsg = errJson.detail.map(d => d.msg || JSON.stringify(d)).join(' · ');
        } catch (parseErr) {
          // Response body was not JSON — fall back to default errMsg
          if (process.env.NODE_ENV !== 'production') console.debug('[consign] error body not JSON:', parseErr);
        }
        alert(errMsg);
        return;
      }

      const data = await response.json();

      if (data.success) {
        setSubmissionId(data.consignment_id);
        setSubmitted(true);
      } else {
        alert('Submission failed. Please try again.');
      }
    } catch (error) {
      devError('Submission error:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch(step) {
      case 1: return Boolean(slots.front && slots.back);
      case 2: return formData.artist_name && formData.title;
      case 3: return Boolean(slots.edition && slots.signature);
      case 4: return formData.name && formData.email;
      default: return false;
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white" data-testid="consign-success-view">
        <Header />
        <div className="pt-32 pb-20 px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto text-center"
          >
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
            <h1 className="text-4xl font-light mb-4">Submission Received</h1>
            <p className="text-xl text-neutral-600 mb-8">
              Thank you for your consignment submission!
            </p>
            
            <div className="bg-neutral-50 p-8 rounded-lg mb-8">
              <p className="text-sm text-neutral-500 mb-2">Submission ID</p>
              <p className="text-2xl font-mono mb-6" data-testid="consign-submission-id">{submissionId}</p>
              
              <div className="text-left space-y-3 text-sm">
                <p className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Our specialists will review your artwork within <strong>7 working days</strong></span>
                </p>
                <p className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>You'll receive an email at <strong>{formData.email}</strong></span>
                </p>
                <p className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>We confirm Pest Control Office certification, provenance, and condition</span>
                </p>
                <p className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>If accepted, we'll discuss consignment terms and timing</span>
                </p>
              </div>
            </div>

            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 bg-black text-white hover:bg-neutral-800 transition-colors"
            >
              Return Home
            </button>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Progress indicator */}
          <ProgressSteps step={step} />

          {/* Step 1: Upload Photos */}
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-3xl font-light mb-4">Upload Artwork Photos</h2>
                <p className="text-neutral-600 mb-8">
                  Add a clear photo of the front and the back of the piece. Our AI will read the front photo to help fill out the form.
                </p>

                {analyzing && (
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6 flex items-center gap-3">
                    <Loader className="w-5 h-5 animate-spin text-blue-600" />
                    <span className="text-blue-900">AI analyzing your artwork...</span>
                  </div>
                )}

                {aiSuggestions && (
                  <div className="bg-green-50 border border-green-200 p-4 rounded-lg mb-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-green-900 mb-2">✓ AI Analysis Complete</p>
                        <p className="text-sm text-green-700">
                          Form pre-filled with detected information. <strong>All fields are editable</strong> - review and adjust as needed.
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setAiSuggestions(null);
                          setFormData({
                            ...formData,
                            artist_name: prefilledArtist,
                            title: '',
                            medium: '',
                            edition_number: '',
                            condition: ''
                          });
                        }}
                        className="text-xs text-green-700 hover:text-green-900 underline ml-4"
                      >
                        Clear AI suggestions
                      </button>
                    </div>
                  </div>
                )}

                {/* Manual Entry Option */}
                <div className="mb-6 flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={skipAI}
                      onChange={(e) => setSkipAI(e.target.checked)}
                      className="w-4 h-4"
                      data-testid="consign-skip-ai-checkbox"
                    />
                    <span>Skip AI analysis (I'll enter details manually)</span>
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6 max-w-md">
                  <PhotoSlot id="front" label="Front" hint="The whole piece, straight on" file={slots.front} onChange={setSlot('front')} />
                  <PhotoSlot id="back" label="Back" hint="Reverse, incl. labels or stamps" file={slots.back} onChange={setSlot('back')} />
                </div>

                <p className="text-sm text-neutral-500 mb-2">More photos (optional): details, frame, condition</p>
                <div className="border-2 border-dashed border-neutral-300 rounded-lg p-5 text-center mb-6">
                  <input
                    type="file"
                    id="artwork-upload"
                    accept="image/*"
                    capture="environment"
                    multiple
                    onChange={(e) => handleImageUpload(e, true)}
                    className="hidden"
                    data-testid="consign-artwork-file-input"
                  />
                  <label htmlFor="artwork-upload" className="cursor-pointer">
                    <Upload className="w-6 h-6 mx-auto mb-2 text-neutral-400" />
                    <p className="text-sm text-neutral-500">Up to 4 extra photos • JPG, PNG • Max 15MB each</p>
                  </label>
                </div>

                {/* Image preview grid */}
                {artworkImages.length > 0 && (
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {artworkImages.map((file, index) => (
                      <div key={`${file.name}-${file.size}-${index}`} className="relative aspect-square">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Artwork ${index + 1}`}
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <button
                          onClick={() => removeImage(index, true)}
                          className="absolute top-2 right-2 w-8 h-8 bg-black/80 text-white rounded-full flex items-center justify-center hover:bg-black"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => setStep(2)}
                  disabled={!canProceed()}
                  className="w-full py-4 bg-black text-white hover:bg-neutral-800 disabled:bg-neutral-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  data-testid="consign-step1-continue-btn"
                >
                  Continue to Details
                  <ArrowRight className="w-5 h-5" />
                </button>
              </motion.div>
            )}

            {/* Step 2: Artwork Details */}
            {step === 2 && (
              <ConsignStep2ArtworkDetails
                formData={formData}
                setFormData={setFormData}
                aiSuggestions={aiSuggestions}
                canProceed={canProceed}
                onBack={() => setStep(1)}
                onContinue={() => setStep(3)}
              />
            )}

            {/* Step 3: Signatures & Marks */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-3xl font-light mb-4">Signatures & Authentication Marks</h2>
                <p className="text-neutral-600 mb-8">
                  Add a close-up of the edition number and of the signature. Extra close-ups of stamps or a certificate of authenticity are welcome.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-6 max-w-md">
                  <PhotoSlot id="edition" label="Edition number" hint="Close-up, e.g. 23/150" file={slots.edition} onChange={setSlot('edition')} />
                  <PhotoSlot id="signature" label="Signature" hint="Close-up of the signature" file={slots.signature} onChange={setSlot('signature')} />
                </div>

                <p className="text-sm text-neutral-500 mb-2">Other close-ups (optional): stamps, COA</p>
                <div className="border-2 border-dashed border-neutral-300 rounded-lg p-5 text-center mb-6">
                  <input
                    type="file"
                    id="mark-upload"
                    accept="image/*"
                    capture="environment"
                    multiple
                    onChange={(e) => handleImageUpload(e, false)}
                    className="hidden"
                  />
                  <label htmlFor="mark-upload" className="cursor-pointer">
                    <Camera className="w-6 h-6 mx-auto mb-2 text-neutral-400" />
                    <p className="text-sm text-neutral-500">Up to 4 extra close-ups • Stamps • COA</p>
                  </label>
                </div>

                {markImages.length > 0 && (
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {markImages.map((file, index) => (
                      <div key={`${file.name}-${file.size}-${index}`} className="relative aspect-square">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Mark ${index + 1}`}
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <button
                          onClick={() => removeImage(index, false)}
                          className="absolute top-2 right-2 w-8 h-8 bg-black/80 text-white rounded-full flex items-center justify-center hover:bg-black"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-4">
                  <button
                    onClick={() => setStep(2)}
                    className="px-6 py-3 border border-neutral-300 hover:border-black transition-colors flex items-center gap-2"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Back
                  </button>
                  <button
                    onClick={() => setStep(4)}
                    disabled={!canProceed()}
                    className="flex-1 py-4 bg-black text-white hover:bg-neutral-800 disabled:bg-neutral-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                    data-testid="consign-step3-continue-btn"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Contact Info */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-3xl font-light mb-4">Contact Information</h2>
                <p className="text-neutral-600 mb-8">
                  We'll contact you at this email within 7 working days.
                </p>

                <div className="space-y-6 mb-8">
                  <div>
                    <label className="block text-sm font-medium mb-2">Full Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-4 py-3 border border-neutral-300 rounded focus:outline-none focus:border-black"
                      placeholder="John Doe"
                      data-testid="consign-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Email Address *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3 border border-neutral-300 rounded focus:outline-none focus:border-black"
                      placeholder="john@example.com"
                      data-testid="consign-email-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Phone Number *</label>
                    <PhoneField
                      value={formData.phone}
                      onChange={(v) => setFormData({ ...formData, phone: v || '' })}
                      required
                      placeholder="Phone number"
                      testIdPrefix="consign-phone"
                    />
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-neutral-50 p-6 rounded-lg mb-8">
                  <h3 className="font-medium mb-4">Submission Summary</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Artwork:</strong> {formData.title || 'Untitled'} by {formData.artist_name || 'Unknown'}</p>
                    <p><strong>Photos:</strong> {artworkImages.length} artwork, {markImages.length} marks</p>
                    <p><strong>Edition:</strong> {formData.edition_number || 'Not specified'}</p>
                    <p><strong>Contact:</strong> {formData.email}</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={() => setStep(3)}
                    className="px-6 py-3 border border-neutral-300 hover:border-black transition-colors flex items-center gap-2"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Back
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={loading || !canProceed()}
                    className="flex-1 py-4 bg-black text-white hover:bg-neutral-800 disabled:bg-neutral-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                    data-testid="consign-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Consignment
                        <CheckCircle className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ConsignPage;
